import type { StorageEngine } from './storageEngine';

export interface MakeStorageOptions<T extends Record<string, unknown>> {
  onSetData?: (data: T, engine: StorageEngine) => void;
  onGetData?: (data: T, engine: StorageEngine) => void;
}

type MakeStorageParams<T extends Record<string, unknown>> = {
  initialValue: T;
  options?: MakeStorageOptions<T>;
  engine: StorageEngine;
};

/**
 * Utility function that creates a memory-based storage using closures
 * @param initialValue Initial value
 * @param options Storage options with hooks
 * @param engine Storage engine implementation
 * @returns Object containing methods for accessing and modifying data
 */
export function makeStorage<T extends Record<string, unknown>>({
  initialValue,
  options,
  engine,
}: MakeStorageParams<T>) {
  let data = initialValue;

  return {
    setData: (nextData: Partial<T>): void => {
      data = { ...data, ...nextData };
      options?.onSetData?.(data, engine);
    },
    getData: (): T => {
      options?.onGetData?.(data, engine);
      return data;
    },
  };
}
